import React from "react";
import { FaCheck, FaStar } from "react-icons/fa";
import { getStripe } from "../../lib/stripe.ts";
import api from "../../api/axios.tsx";

type Tier = {
  name: string;
  price: string;
  period?: string;
  highlight?: boolean;
  features: string[];
  cta: string;
};

const tiers: Tier[] = [
  {
    name: "Essential",
    price: "$0",
    period: "/forever",
    features: [
      "Smart Dashboard",
      "Expense Tracking",
      "Detailed Budgets (limit 2)",
      "Financial Goals (limit 2)",
      "Mobile-Friendly Design",
      "Data Privacy & Encryption",
      "Recurring Transactions (limit 5)",
      "Export & Backup (1 export/month)",
      "Smart Notifications",
    ],
    cta: "Get Started",
  },
  {
    name: "Advanced",
    price: "$9",
    period: "/mo",
    highlight: true,
    features: [
      "All Essential features (unlimited)",
      "Borrow & Lend Tracking",
      "Split Bills with Friends",
      "Multi-Currency Support",
      "Loan & EMI Calculator",
      "Offline Mode",
      "Export & Backup (unlimited)",
      "Receipt Scanner",
      "Financial Health Score",
      "Enhanced Smart Notifications",
    ],
    cta: "Start Advanced",
  },
  {
    name: "Intelligent",
    price: "$19",
    period: "/mo",
    features: [
      "All Advanced features",
      "Insights & Recommendations (AI-driven)",
      "Cash Flow Forecasting",
      "Goal Optimization with AI",
      "Auto-Budgeting",
      "What-If Simulator",
      "Chatbot Assistant",
      "Location-Aware Suggestions",
      "Anomaly Detection",
      "Priority Support",
    ],
    cta: "Go Intelligent",
  },
];

const Pricing = () => {
  const handleSelect = async (tierName: string) => {
    if (tierName === "Essential") {
      // Free plan: send to register/login or dashboard
      window.location.href = "/register";
      return;
    }

    const serverUrl = process.env.REACT_APP_API_BASE_URL || "";
    const base = serverUrl.endsWith("/") ? serverUrl.slice(0, -1) : serverUrl;

    try {
      // Try primary path, then fallback to our local server path
      let res = await api.post(`${base}/payments/create_checkout_session`, {
        tier: tierName,
        mode: "subscription",
        priceId:
          tierName === "Advanced"
            ? process.env.REACT_APP_STRIPE_PRICE_ADVANCED
            : process.env.REACT_APP_STRIPE_PRICE_INTELLIGENT,
      });

      // Optionally inspect response in development

      if (res.status < 200 || res.status >= 300)
        throw new Error("Failed to create checkout session");
      const data = res.data;

      // Prefer redirectToCheckout if publishable key is set; otherwise, fallback to session URL
      const stripe = await getStripe();
      if (stripe && data.id) {
        const { error } = await stripe.redirectToCheckout({
          sessionId: data.id,
        });
        if (error) {
          console.error(error);
          // Fallback to direct URL if available
          if (data.url) window.location.href = data.url;
        }
      } else if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No session URL provided by server");
      }
    } catch (e) {
      console.error(e);
      alert("Unable to start checkout. Please try again later.");
    }
  };

  return (
    <section id="pricing" className="section">
      <div className="container flex justify-center mb-12 max-w-[1440px] mx-auto">
        {/* <div
          className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-sky-500/5 to-transparent"
          aria-hidden
        /> */}
        <div className="container-site text-center h-max">
          <span className="inline-flex items-center gap-2 eyebrow mb-3">
            <span className="w-4 h-4 text-yellow-300">
              <FaStar />
            </span>
            Flexible Pricing
          </span>
          <h2 className="heading-2">Choose Your Plan</h2>
          <p className="subheading">
            Start free. Upgrade anytime as your needs grow.
          </p>

          {/* Cards grid with equal height for cards */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 h-full">
            {tiers.map((t) => (
              <div
                key={t.name}
                className={[
                  "relative card-glass p-6 text-left shadow-lg transition-transform hover:-translate-y-1 flex flex-col h-full",
                  t.highlight ? "ring-1 ring-indigo-400/40" : "",
                ].join(" ")}
              >
                {t.highlight && (
                  <div className="absolute -top-3 right-4 text-xs px-2 py-1 rounded-full text-white shadow ring-1 ring-white/10 bg-indigo-600">
                    Popular
                  </div>
                )}
                <h3 className="text-white font-extrabold text-xl">{t.name}</h3>
                <div className="mt-2 flex items-end gap-1">
                  <span className="text-4xl font-extrabold text-white">
                    {t.price}
                  </span>
                  {t.period && (
                    <span className="text-gray-400 text-sm mb-1">
                      {t.period}
                    </span>
                  )}
                </div>
                <ul className="mt-6 space-y-3 flex-grow">
                  {t.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-3 text-gray-200"
                    >
                      <span className="mt-1 w-4 h-4 text-emerald-400">
                        <FaCheck />
                      </span>
                      <span className="text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                {/* Button sticks to the bottom via flex and mt-auto */}
                <button
                  className={[
                    "mt-8 w-full",
                    t.highlight ? "btn-primary" : "btn-secondary",
                  ].join(" ")}
                  onClick={() => handleSelect(t.name)}
                >
                  {t.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
